import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Pager } from '../../components/Pager';
import { SystemLogFilterBar } from './SystemLogFilterBar';
import { SystemLogTable } from './SystemLogTable';
import { SystemLogDetail } from './SystemLogDetail';
import {
  useAckEventMutation,
  useSystemLogDetail,
  useSystemLogList,
} from './useSystemLog';
import {
  DEFAULT_FILTERS,
  filtersFromSearchParams,
  filtersToSearchParams,
  type SystemLogFilters,
} from './systemLog.types';
import { useAuthStore } from '../../stores/authStore';
import './systemLog.css';

// 권위: docs/ARCH_DASHBOARD.md §4.8 (화면 8) — 컨테이너.
// - 필터 상태 ↔ URL 쿼리스트링 동기화 (북마크/공유 가능).
// - `/logs/:eventId` 딥링크 → 상세 모달 자동 오픈. 닫으면 `/logs` 로 되돌림.
// - 실시간 WS prepend 는 useSystemLogList 가 담당.

export default function SystemLogPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const { eventId } = useParams<{ eventId?: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  // 필터는 URL 쿼리스트링에서 단일 진실원.
  const filters = useMemo<SystemLogFilters>(
    () => filtersFromSearchParams(searchParams),
    [searchParams]
  );

  const setFilters = useCallback(
    (next: SystemLogFilters) => {
      setSearchParams(filtersToSearchParams(next), { replace: true });
    },
    [setSearchParams]
  );

  const onReset = useCallback(() => {
    setSearchParams(filtersToSearchParams(DEFAULT_FILTERS), { replace: true });
  }, [setSearchParams]);

  const list = useSystemLogList(filters);
  const items = list.data?.items ?? [];
  const total = list.data?.total ?? 0;

  // ── 딥링크 상세 모달 ───────────────────────────────
  const detail = useSystemLogDetail(eventId ?? null);
  const ack = useAckEventMutation(user?.email ?? 'unknown');

  const closeDetail = useCallback(() => {
    // /logs 로 돌아가되, 현재 필터 쿼리스트링 은 유지.
    navigate(
      { pathname: '/logs', search: searchParams.toString() ? `?${searchParams.toString()}` : '' },
      { replace: false }
    );
  }, [navigate, searchParams]);

  // ESC 로 모달 닫기.
  useEffect(() => {
    if (!eventId) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') closeDetail();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [eventId, closeDetail]);

  return (
    <div className="syslog-page">
      <header className="syslog-page__header">
        <h1 className="syslog-page__title">시스템 로그</h1>
        <div className="syslog-page__meta">
          총 {total.toLocaleString('ko-KR')} 건
          {list.data?.unackedCount !== undefined && list.data.unackedCount > 0 && (
            <> · 미확인 {list.data.unackedCount.toLocaleString('ko-KR')}</>
          )}
          {list.isFetching && <span className="syslog-muted"> · 갱신 중…</span>}
        </div>
      </header>

      <SystemLogFilterBar filters={filters} onChange={setFilters} onReset={onReset} />

      {list.isError && (
        <div className="syslog-warning-banner">
          목록 로드 실패: {list.error?.message ?? 'unknown'}
        </div>
      )}

      <SystemLogTable
        items={items}
        isLoading={list.isLoading}
        realtimeCount={list.realtimeCount}
        onRowClick={(it) => {
          navigate({
            pathname: `/logs/${it.eventId}`,
            search: searchParams.toString() ? `?${searchParams.toString()}` : '',
          });
        }}
      />

      <Pager
        page={filters.page}
        pageSize={filters.pageSize}
        total={total}
        onPageChange={(p) => setFilters({ ...filters, page: p })}
      />


      {eventId && (
        <SystemLogDetail
          detail={detail.data ?? null}
          isLoading={detail.isLoading}
          isError={detail.isError}
          error={(detail.error as Error | null) ?? null}
          ackBusy={ack.isPending}
          onClose={closeDetail}
          onAck={(note) => {
            ack.mutate({ eventId, note: note || undefined });
          }}
        />
      )}
    </div>
  );
}
